import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from '../../components/common/FilterDropdown';
import { GenericView, SearchInput, ActionButtons } from '../../components/common/table';
import ExpenseCategoryForm from './components/ExpenseCategoryForm';
import ViewExpenseCategory from './components/ViewExpenseCategory';
import Alert from '../../components/Alert/Alert';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { getExpenseCategories, deleteExpenseCategory, updateExpenseCategory, createExpenseCategory } from './api/expenseCategoryApi';

const ExpenseCategory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterData, setFilterData] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const showAlert = (type, message) => {
    setAlert({ type, message });
    setTimeout(() => setAlert(null), 5000); // Auto hide after 5 seconds
  };

  useEffect(() => {
    loadExpenseCategories();
  }, []);

  const loadExpenseCategories = async () => {
    setIsLoading(true);
    try {
      const response = await getExpenseCategories();
      const data = Array.isArray(response) ? response : response.data || [];
      setExpenseCategories(data);
    } catch (error) {
      console.error('Error loading expense categories:', error);
      showAlert('danger', 'Failed to load expense categories.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilterData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterReset = () => {
    setFilterData({});
  };

  const handleFilterApply = () => {
    // Add your filter logic here if needed
  };

  // Filter and search data
  const filteredData = expenseCategories.filter(item => {
    const matchesSearch = !searchQuery || 
      (item.name && item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesSearch;
  });

  const columns = [
    { 
      key: 'name', 
      label: 'Name',
      render: (value) => value || '-'
    },
    { 
      key: 'cdate', 
      label: 'Create Date',
      render: (value) => value ? new Date(value).toLocaleString() : '-'
    }
  ];

  const handleFormSubmit = async (formData) => {
    const payload = {
      name: formData.name
    };
    try {
      if (selectedCategory) {
        await updateExpenseCategory(selectedCategory.id, payload);
        showAlert('success', 'Expense category updated successfully!');
      } else {
        await createExpenseCategory(payload);
        showAlert('success', 'Expense category created successfully!');
      }
      await loadExpenseCategories();
      setIsModalOpen(false);
      setSelectedCategory(null);
    } catch (error) {
      console.error('Error saving expense category:', error);
      showAlert('danger', 'Failed to save expense category. Please check your input.');
    }
  };

  const handleEdit = (row) => {
    setSelectedCategory(row);
    setIsModalOpen(true);
  };

  const handleDelete = async (row) => {
    setItemToDelete(row);
    setShowDeleteModal(true);
  };

  const handleView = (row) => {
    setSelectedCategory(row);
    setIsViewModalOpen(true);
  };

  return (
    <div className="container-xl">
      {/* Alert Component */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          onClose={() => setAlert(null)}
        />
      )}

      <div className="row g-2 align-items-center">
        <div className="col">
          <h3 className="page-title m-0">Expense Category Management</h3>
        </div>
        <div className="col-auto ms-auto d-print-none">
          <div className="btn-list">
            <button 
              className="btn btn-primary"
              onClick={() => {
                setSelectedCategory(null);
                setIsModalOpen(true);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-plus" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 5l0 14"></path>
                <path d="M5 12l14 0"></path>
              </svg>
              Create
            </button>
          </div>
        </div>
      </div>

      <ExpenseCategoryForm
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedCategory(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={selectedCategory}
      />

      <ViewExpenseCategory
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setSelectedCategory(null);
        }}
        data={selectedCategory}
      />

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        onConfirm={async () => {
          if (!itemToDelete?.id) return;
          setIsLoading(true);
          try {
            await deleteExpenseCategory(itemToDelete.id);
            showAlert('success', 'Expense category deleted successfully!');
            await loadExpenseCategories();
          } catch (error) {
            console.error('Error deleting expense category:', error);
            showAlert('danger', 'Failed to delete expense category.');
          } finally {
            setIsLoading(false);
          }
          setShowDeleteModal(false);
          setItemToDelete(null);
        }}
        title="Delete Expense Category"
        message={`Are you sure you want to delete "${itemToDelete?.name || 'this category'}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <div className="page-body">
        <div className="container-xl">
          <div className="col">
            <div className="card">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs" data-bs-toggle="tabs" role="tablist">
                  <li className="nav-item-tab" role="presentation">
                    <a href="#tabs-list-data" className="nav-link active" data-bs-toggle="tab" aria-selected="true" role="tab">List Data</a>
                  </li>
                </ul>
              </div>
              <div className="card-body">
                <div className="tab-content">
                  <div className="tab-pane active show" id="tabs-list-data" role="tabpanel">
                    <GenericView
                      data={filteredData}
                      columns={columns}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onView={handleView}
                      isLoading={isLoading}
                      filterComponent={
                        <FilterDropdown
                          filterData={filterData}
                          onFilterChange={handleFilterChange}
                          onReset={handleFilterReset}
                          onApply={handleFilterApply}
                        />
                      }
                      searchComponent={
                        <SearchInput
                          value={searchQuery}
                          onChange={setSearchQuery}
                          placeholder="Search expense categories..."
                        />
                      }
                      actionButtons={
                        <ActionButtons
                          onExportExcel={() => {
                            showAlert('info', 'Export to Excel functionality coming soon');
                          }}
                          onExportPDF={() => {
                            showAlert('info', 'Export to PDF functionality coming soon');
                          }}
                        />
                      }
                      emptyStateMessage="No expense categories found"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCategory;

